import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Brain, Flame, ArrowLeft, RefreshCw, MessageCircle, Bell, Plus, X, Target, Mic, MicOff, Volume2, VolumeX, Square, Activity, TrendingUp, TrendingDown } from "lucide-react";
import { isWebSpeechAvailable, listenWithWebSpeech, startRecordingForWhisper, speakHighQuality, speakForge, VOICE_OPTIONS } from "@/lib/companionVoice";
import { WakeWordToggle } from "@/components/WakeWord";


const MOOD_EMOJI = {
  elated: "🤩", happy: "😄", focused: "🧐",
  concerned: "😟", worried: "😰", sleepy: "😴",
};


export default function Companion() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [factInput, setFactInput] = useState("");
  const [facts, setFacts] = useState([]);
  const [listening, setListening] = useState(false);
  const [autoSend, setAutoSend] = useState(false);   // auto-send after transcription
  const [autoSpeak, setAutoSpeak] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("forge_auto_speak") === "1";
  });
  const [voice, setVoice] = useState(() => {
    if (typeof window === "undefined") return "nova";
    return window.localStorage.getItem("forge_voice") || "nova";
  });
  const [speaking, setSpeaking] = useState(false);
  const speakStopRef = useRef(null);
  const spokenIdsRef = useRef(new Set());     // avoid re-speaking on reload / status refresh
  const listenerRef = useRef(null);                   // { stop, cancel } handle
  const scrollRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("forge_auto_speak", autoSpeak ? "1" : "0");
    }
  }, [autoSpeak]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("forge_voice", voice);
    }
  }, [voice]);

  const stopSpeaking = useCallback(() => {
    try { speakStopRef.current?.(); } catch { /* noop */ }
    speakStopRef.current = null;
    setSpeaking(false);
  }, []);

  // Auto-speak: whenever a new assistant message appears (and we haven't spoken it yet),
  // if autoSpeak is enabled, play it out loud. Silently skip on any TTS error.
  useEffect(() => {
    if (!autoSpeak || busy) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || !last.content) return;
    const key = `${last.created_at || ""}::${(last.content || "").slice(0, 40)}`;
    if (spokenIdsRef.current.has(key)) return;
    spokenIdsRef.current.add(key);
    let cancelled = false;
    (async () => {
      stopSpeaking();
      setSpeaking(true);
      try {
        const handle = await speakForge(last.content, {
          voice,
          onEnd: () => setSpeaking(false),
        });
        if (cancelled) { handle.stop(); return; }
        speakStopRef.current = handle.stop;
      } catch (err) {
        console.debug("[voice] auto-speak error", err);
        setSpeaking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [messages, autoSpeak, voice, busy, stopSpeaking]);

  // Cleanup on unmount
  useEffect(() => () => stopSpeaking(), [stopSpeaking]);

  const load = useCallback(async () => {
    try {
      const [s, h, m] = await Promise.all([
        api.get("/companion/status"),
        api.get("/companion/history", { params: { limit: 100 } }),
        api.get("/companion/memory"),
      ]);
      setStatus(s.data);
      const history = h.data.messages || [];
      // Pre-mark all history so we don't auto-speak old messages on reload
      for (const msg of history) {
        if (msg.role === "assistant") {
          const key = `${msg.created_at || ""}::${(msg.content || "").slice(0, 40)}`;
          spokenIdsRef.current.add(key);
        }
      }
      setMessages(history);
      setFacts(m.data.facts || []);
    } catch { toast.error("Companion unavailable"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, created_at: new Date().toISOString(), _temp: true }]);
    setBusy(true);
    try {
      const { data } = await api.post("/companion/chat", { message: msg });
      setMessages((m) => [...m.filter(x => !x._temp), { role: "user", content: msg, created_at: new Date().toISOString() },
                          { role: "assistant", content: data.content, created_at: new Date().toISOString() }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forge is offline");
      setMessages((m) => m.filter(x => !x._temp));
    } finally { setBusy(false); }
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear ALL chat history with Forge? (Memory of learned facts is kept.)")) return;
    await api.delete("/companion/history"); setMessages([]); toast.success("History cleared");
  };

  const sendWithText = React.useCallback(async (msg) => {
    const trimmed = msg.trim();
    if (!trimmed || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: trimmed, created_at: new Date().toISOString(), _temp: true }]);
    setBusy(true);
    try {
      const { data } = await api.post("/companion/chat", { message: trimmed });
      setMessages((m) => [...m.filter(x => !x._temp),
        { role: "user", content: trimmed, created_at: new Date().toISOString() },
        { role: "assistant", content: data.content, created_at: new Date().toISOString() }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forge is offline");
      setMessages((m) => m.filter(x => !x._temp));
    } finally { setBusy(false); }
  }, [busy]);

  const toggleMic = async () => {
    if (listening) {
      // Stop and get transcript
      try {
        const handle = listenerRef.current;
        const text = handle ? await handle.stop() : "";
        setListening(false);
        listenerRef.current = null;
        if (text) {
          setInput((prev) => (prev ? `${prev} ${text}` : text));
          if (autoSend) await sendWithText(text);
        }
      } catch (err) {
        setListening(false);
        listenerRef.current = null;
        if (err?.code !== "aborted") toast.error("Couldn't understand — try again");
      }
      return;
    }
    // Start listening — Web Speech first, fall back to MediaRecorder + Whisper
    setListening(true);
    try {
      if (isWebSpeechAvailable()) {
        const h = listenWithWebSpeech();
        listenerRef.current = { stop: async () => { h.stop(); return await h.promise; }, cancel: h.cancel };
      } else {
        const rec = await startRecordingForWhisper();
        listenerRef.current = rec;
      }
    } catch (err) {
      setListening(false);
      const denied = err?.name === "NotAllowedError" || err?.code === "denied";
      toast.error(denied ? "Microphone permission denied" : "Voice input unavailable");
    }
  };

  const addFact = async () => {
    const fact = factInput.trim();
    if (fact.length < 3) return;
    try {
      const { data } = await api.post("/companion/remember", { fact });
      setFacts(data.facts || []); setFactInput("");
      toast.success("I'll remember that.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save fact"); }
  };

  const clearFacts = async () => {
    if (!window.confirm("Forget everything Forge has learned about you?")) return;
    await api.delete("/companion/memory"); setFacts([]); toast.success("Memory wiped");
  };

  if (!status) return <div className="min-h-screen"><Navbar/><div className="p-10 text-[var(--text-muted)] font-mono">Waking Forge…</div></div>;

  return (
    <div className="min-h-screen bg-[var(--bg-dark)]" data-testid="companion-page">
      <Navbar/>
      <main className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Chat column */}
        <section className="card-sharp flex flex-col min-h-[75vh]" data-testid="companion-chat">
          <header className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
            <button onClick={()=>nav(-1)} className="p-1.5 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)]"><ArrowLeft size={13}/></button>
            <div className="text-3xl leading-none" data-testid="companion-mood">{MOOD_EMOJI[status.mood] || "🤖"}</div>
            <div className="flex-1">
              <h1 className="font-display text-xl text-white">{status.name}</h1>
              <p className="text-[11px] font-mono text-[var(--text-muted)]">Your trading companion · {status.mood}</p>
            </div>
            <button data-testid="companion-refresh" onClick={load}
              className="p-2 border border-white/10 rounded hover:border-[var(--accent)]"><RefreshCw size={13}/></button>
            <WakeWordToggle/>
            {/* Auto-speak toggle */}
            <button data-testid="companion-autospeak-toggle"
              onClick={() => { const next = !autoSpeak; setAutoSpeak(next); if (!next) stopSpeaking(); }}
              aria-pressed={autoSpeak}
              title={autoSpeak ? "Auto-speak ON — Forge reads replies aloud" : "Auto-speak OFF — click to enable"}
              className={`p-2 border rounded transition-colors flex items-center gap-1 text-[10px] font-mono ${
                autoSpeak
                  ? "!border-[var(--accent)] !text-[var(--accent)] bg-[var(--accent)]/10"
                  : "border-white/10 hover:border-[var(--accent)] hover:text-[var(--accent)]"
              }`}>
              {autoSpeak ? <Volume2 size={13}/> : <VolumeX size={13}/>}
              <span className="hidden sm:inline">{autoSpeak ? "ON" : "OFF"}</span>
            </button>
            {/* Voice picker */}
            <select data-testid="companion-voice-select"
              value={voice} onChange={(e) => setVoice(e.target.value)}
              title="Choose Forge's voice"
              className="input-dark !py-1.5 !px-2 !text-[10px] font-mono w-24 hidden md:block">
              {VOICE_OPTIONS.map(v => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
            {/* Stop speaking (only while playing) */}
            {speaking && (
              <button data-testid="companion-stop-speaking"
                onClick={stopSpeaking}
                title="Stop reading"
                className="p-2 border !border-[var(--loss)] !text-[var(--loss)] rounded animate-pulse">
                <Square size={13}/>
              </button>
            )}
            <button data-testid="companion-clear-history" onClick={clearHistory}
              className="p-2 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><Trash2 size={13}/></button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-10" data-testid="companion-empty">
                <div className="text-5xl mb-3">{MOOD_EMOJI[status.mood]}</div>
                <h2 className="font-display text-xl text-white mb-1">{status.greeting}</h2>
                <p className="text-[var(--text-muted)] text-sm max-w-md mx-auto font-mono">
                  Ask about your recent trades, get an opinion on a setup, or just vent about a rough day.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                  {[
                    "Should I take a EURUSD long right now?",
                    "How's my week been?",
                    "Am I overtrading?",
                    "Review my last 5 losses",
                  ].map(q => (
                    <button key={q} data-testid={`prompt-${q.slice(0,10)}`}
                      onClick={()=>setInput(q)}
                      className="chip hover:!border-[var(--accent)] hover:!text-[var(--accent)] text-left cursor-pointer">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={`${m.created_at}-${i}`} msg={m} mood={status.mood} voice={voice}/>)}
            {busy && <Bubble msg={{ role: "assistant", content: "…" }} mood={status.mood} voice={voice} thinking/>}
          </div>

          <div className="border-t border-white/5 p-3 flex flex-col gap-2">
            <div className="flex gap-2">
              <button data-testid="companion-mic-btn"
                onClick={toggleMic}
                disabled={busy}
                title={listening ? "Stop listening" : "Talk to Forge (mic)"}
                className={`p-2 border rounded transition-colors ${
                  listening
                    ? "!border-[var(--loss)] !text-[var(--loss)] animate-pulse"
                    : "border-white/10 hover:border-[var(--accent)] hover:text-[var(--accent)]"
                }`}>
                {listening ? <MicOff size={13}/> : <Mic size={13}/>}
              </button>
              <input data-testid="companion-input"
                value={input} onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={listening ? "Listening…" : "Talk to Forge…"}
                disabled={busy}
                className="input-dark flex-1"/>
              <button data-testid="companion-send" onClick={send} disabled={busy || !input.trim()}
                className="btn-primary flex items-center gap-2 px-4">
                <Send size={13}/> {busy ? "…" : "Send"}
              </button>
            </div>
            <label className="flex items-center gap-2 text-[10px] font-mono text-[var(--text-muted)] cursor-pointer self-start">
              <input data-testid="companion-auto-send-toggle" type="checkbox"
                checked={autoSend} onChange={(e)=>setAutoSend(e.target.checked)}
                className="accent-[var(--accent)]"/>
              Auto-send after voice input
            </label>
          </div>
        </section>

        {/* Sidebar: streak, goals, memory */}
        <aside className="space-y-4">
          <div className="card-sharp p-4" data-testid="companion-streak-card">
            <div className="flex items-center gap-2 mb-2">
              <Flame size={14} className="text-[var(--accent)]"/>
              <h3 className="font-display text-white">Streak & XP</h3>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-white/10 rounded p-2">
                <div className="text-2xl font-display text-white">{status.streak.streak}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Day streak</div>
              </div>
              <div className="border border-white/10 rounded p-2">
                <div className="text-2xl font-display text-[var(--accent)]">{status.streak.xp}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)]">XP</div>
              </div>
              <div className="border border-white/10 rounded p-2">
                <div className="text-2xl font-display text-white">{status.streak.level}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Level</div>
              </div>
            </div>
          </div>

          <div className="card-sharp p-4" data-testid="companion-goals-card">
            <h3 className="font-display text-white mb-3">This week's goals</h3>
            <ul className="space-y-2">
              {(status.weekly_goals || []).map(g => (
                <li key={g.id} className="text-xs font-mono" data-testid={`goal-${g.id}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${g.done ? "bg-[var(--profit)]" : "bg-white/20"}`}/>
                    <span className={g.done ? "text-[var(--profit)] line-through" : "text-white"}>{g.title}</span>
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] ml-5 mt-0.5">
                    {g.invert ? `${g.current} logged (target: ≤${g.target})` : `${g.current} / ${g.target}`}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <WatchlistSidebarCard/>

          <div className="card-sharp p-4" data-testid="companion-memory-card">
            <div className="flex items-center gap-2 mb-2">
              <Brain size={14} className="text-[var(--accent)]"/>
              <h3 className="font-display text-white flex-1">What Forge remembers</h3>
              {facts.length > 0 && (
                <button data-testid="companion-forget-all" onClick={clearFacts}
                  className="text-[10px] font-mono text-[var(--text-muted)] hover:text-[var(--loss)]">Forget all</button>
              )}
            </div>
            {facts.length === 0 ? (
              <p className="text-[11px] font-mono text-[var(--text-muted)] italic">
                Teach me facts about your trading style so I can give better advice.
              </p>
            ) : (
              <ul className="space-y-1 mb-3">
                {facts.map((f, i) => (
                  <li key={f} className="text-[11px] font-mono text-[var(--text-secondary)]" data-testid={`fact-${i}`}>
                    · {f}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-1 mt-2">
              <input data-testid="fact-input"
                value={factInput} onChange={(e)=>setFactInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter") addFact(); }}
                placeholder="I only trade London session…"
                className="input-dark flex-1 !text-xs !py-1.5"/>
              <button data-testid="fact-add" onClick={addFact}
                className="btn-outline text-xs !py-1.5 px-3">Save</button>
            </div>
          </div>

          <PriceAlertsCard/>
        </aside>
      </main>
    </div>
  );
}


function Bubble({ msg, mood, voice = "nova", thinking }) {
  const isUser = msg.role === "user";
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef(null);

  const speak = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    try {
      audioRef.current = await speakHighQuality(msg.content, { voice });
      audioRef.current.addEventListener("ended", () => setPlaying(false), { once: true });
    } catch (err) {
      setPlaying(false);
      toast.error(err?.message || "Voice unavailable");
    }
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`}
      data-testid={`bubble-${msg.role}`}>
      {!isUser && <div className="text-2xl leading-none pt-1 select-none">{MOOD_EMOJI[mood] || "🤖"}</div>}
      <div className={`max-w-[75%] px-4 py-2.5 rounded-lg ${isUser
        ? "bg-[var(--accent)] text-black"
        : "bg-black/40 border border-white/10 text-[var(--text-primary)]"}`}>
        <div className={`whitespace-pre-wrap text-sm ${thinking ? "opacity-60" : ""}`}>
          {thinking ? <TypingDots/> : msg.content}
        </div>
        {!isUser && !thinking && msg.content && msg.content.length > 3 && (
          <button data-testid="bubble-speak-btn" onClick={speak}
            title={playing ? "Stop" : "Read aloud"}
            className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors flex items-center gap-1">
            {playing ? <VolumeX size={10}/> : <Volume2 size={10}/>}
            {playing ? "Stop" : "Speak"}
          </button>
        )}
      </div>
    </div>
  );
}


function TypingDots() {
  return (
    <span className="inline-flex gap-1">
      <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce"/>
      <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce" style={{animationDelay:"0.15s"}}/>
      <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce" style={{animationDelay:"0.3s"}}/>
    </span>
  );
}


function PriceAlertsCard() {
  const [alerts, setAlerts] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ symbol: "EURUSD", condition: "above", price: "", note: "" });
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try { const { data } = await api.get("/alerts"); setAlerts(data); }
    catch (err) { console.debug("[alerts] load failed", err?.response?.status || err); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.symbol.trim() || !form.price) { toast.error("Symbol and price required"); return; }
    setBusy(true);
    try {
      await api.post("/alerts", {
        symbol: form.symbol.trim().toUpperCase(),
        condition: form.condition,
        price: parseFloat(form.price),
        note: form.note.trim() || undefined,
      });
      toast.success("Alert armed");
      setShowForm(false); setForm({ symbol: "EURUSD", condition: "above", price: "", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create alert"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/alerts/${id}`); load();
  };
  const rearm = async (id) => {
    await api.patch(`/alerts/${id}`, { status: "active" }); toast.success("Alert re-armed"); load();
  };

  const active = alerts.filter(a => a.status === "active");
  const triggered = alerts.filter(a => a.status === "triggered");

  return (
    <div className="card-sharp p-4" data-testid="companion-alerts-card">
      <div className="flex items-center gap-2 mb-3">
        <Bell size={14} className="text-[var(--accent)]"/>
        <h3 className="font-display text-white flex-1">Price alerts</h3>
        <button data-testid="alert-add-btn" onClick={()=>setShowForm(!showForm)}
          className="p-1 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)]">
          {showForm ? <X size={11}/> : <Plus size={11}/>}
        </button>
      </div>

      {showForm && (
        <div className="border border-white/10 rounded p-2.5 mb-3 space-y-2 bg-black/30" data-testid="alert-form">
          <div className="flex gap-1">
            <input data-testid="alert-symbol"
              className="input-dark flex-1 !text-xs !py-1.5"
              placeholder="EURUSD"
              value={form.symbol}
              onChange={(e)=>setForm({...form, symbol: e.target.value.toUpperCase()})}/>
            <select data-testid="alert-condition"
              className="input-dark !text-xs !py-1.5 w-24"
              value={form.condition}
              onChange={(e)=>setForm({...form, condition: e.target.value})}>
              <option value="above">≥</option>
              <option value="below">≤</option>
              <option value="crosses">cross</option>
            </select>
          </div>
          <input data-testid="alert-price" type="number" step="0.00001"
            placeholder="1.0850" className="input-dark w-full !text-xs !py-1.5"
            value={form.price}
            onChange={(e)=>setForm({...form, price: e.target.value})}/>
          <input data-testid="alert-note"
            placeholder="Optional note"
            className="input-dark w-full !text-xs !py-1.5"
            value={form.note}
            onChange={(e)=>setForm({...form, note: e.target.value})}/>
          <button data-testid="alert-create-btn" onClick={create} disabled={busy}
            className="btn-primary w-full text-xs !py-1.5 flex items-center justify-center gap-1">
            <Target size={11}/> {busy ? "…" : "Arm alert"}
          </button>
        </div>
      )}

      {alerts.length === 0 ? (
        <p className="text-[11px] font-mono text-[var(--text-muted)] italic">
          Set an alert to have Forge ping you when a price is hit.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {[...active, ...triggered].map(a => (
            <li key={a.alert_id}
              className={`flex items-center gap-2 text-[11px] font-mono border rounded px-2 py-1.5
                ${a.status === "triggered" ? "border-[var(--profit)]/40 bg-[var(--profit)]/5" : "border-white/10"}`}
              data-testid={`alert-${a.alert_id}`}>
              <span className={`w-2 h-2 rounded-full shrink-0
                ${a.status === "triggered" ? "bg-[var(--profit)]" : a.status === "waiting_for_broker" ? "bg-[var(--warning)]" : "bg-[var(--accent)] animate-pulse"}`}/>
              <span className="text-white truncate">
                {a.symbol} {a.condition === "above" ? "≥" : a.condition === "below" ? "≤" : "×"} {a.price}
              </span>
              {a.status === "triggered" && (
                <button data-testid={`alert-rearm-${a.alert_id}`} onClick={()=>rearm(a.alert_id)}
                  className="text-[var(--accent)] hover:underline ml-auto">re-arm</button>
              )}
              <button data-testid={`alert-del-${a.alert_id}`} onClick={()=>del(a.alert_id)}
                className={a.status === "triggered" ? "text-[var(--text-muted)] hover:text-[var(--loss)]" : "text-[var(--text-muted)] hover:text-[var(--loss)] ml-auto"}>
                <X size={11}/>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



function WatchlistSidebarCard() {
  const [symbols, setSymbols] = React.useState([]);
  const [quotes, setQuotes] = React.useState({});

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/market/watchlist");
        if (cancelled) return;
        const syms = data.symbols || [];
        setSymbols(syms);
        for (const s of syms) {
          try {
            const q = await api.get(`/market/quote/${s}`);
            if (!cancelled) setQuotes((prev) => ({ ...prev, [s]: q.data }));
          } catch { /* symbol has no data yet */ }
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (symbols.length === 0) return null;

  return (
    <div className="card-sharp p-4" data-testid="companion-watchlist-card">
      <div className="flex items-center gap-2 mb-2">
        <Activity size={14} className="text-[var(--accent)]"/>
        <h3 className="font-display text-white flex-1">Daily H/L</h3>
      </div>
      <ul className="space-y-2">
        {symbols.map((s) => {
          const q = quotes[s];
          const brokeHigh = q?.today && q?.prior && q.today.high > q.prior.high;
          const brokeLow  = q?.today && q?.prior && q.today.low  < q.prior.low;
          return (
            <li key={s} className="text-[11px] font-mono border border-white/10 rounded px-2 py-1.5"
              data-testid={`sidebar-wl-${s}`}>
              <div className="flex items-center gap-2">
                <span className="text-white font-display">{s}</span>
                {brokeHigh && <TrendingUp size={11} className="text-[var(--profit)]"/>}
                {brokeLow  && <TrendingDown size={11} className="text-[var(--loss)]"/>}
                <span className="ml-auto text-[var(--text-secondary)]">
                  {q?.today?.close?.toFixed(5) ?? "—"}
                </span>
              </div>
              {q?.today && (
                <div className="flex justify-between text-[10px] text-[var(--text-muted)] mt-0.5">
                  <span className="text-[var(--profit)]">H {q.today.high?.toFixed(5)}</span>
                  <span className="text-[var(--loss)]">L {q.today.low?.toFixed(5)}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
